import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Input,
  Upload,
  Button,
  message,
  Select,
  InputNumber,
  Switch,
  Tabs,
  Spin,
  ColorPicker,
} from "antd";
import {
  UploadOutlined, SaveOutlined, GlobalOutlined, DollarOutlined, SkinOutlined, FileTextOutlined, ShopOutlined, WalletOutlined, CalendarOutlined,
} from "@ant-design/icons";
import { readCachedSystemConfig, fetchSystemConfig, saveSystemConfig, DEFAULT_PRINT_COLORS } from "../../../shared/lib/systemConfig";
import { DATE_FILTER_PRESET_DEFS, DEFAULT_DATE_FILTER_PRESETS, normalizeDateFilterPresets } from "../../../shared/lib/dateFilterPresets";
import { fetchExpenseTypes } from "../../purchasing/lib/expenseTypes";
import { LANGUAGES } from "./Language";
import i18nModule from "../../../i18";
import packageJson from "../../../../package.json";
import { ListPage, FormCard, T, inputStyle, grid2, labelStyle } from "../../../shared/ui/PageShell";
import usePermissions from "../../../hooks/usePermissions";

const UploadField = ({ label, fileList, onChange, hint }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ ...labelStyle, marginBottom: 6 }}>{label}</div>
    <Upload fileList={fileList} beforeUpload={() => false} onChange={({ fileList: fl }) => onChange(fl.slice(-1))} maxCount={1} accept="image/*">
      <Button icon={<UploadOutlined />} style={{ borderRadius: 8 }}>{hint}</Button>
    </Upload>
  </div>
);

const SystemSettings = () => {
  const { t } = useTranslation();
  const { isAdmin } = usePermissions();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loginFileList, setLoginFileList] = useState([]);
  const [sidebarFileList, setSidebarFileList] = useState([]);
  const [invoiceLogoFileList, setInvoiceLogoFileList] = useState([]);
  const [invoiceStampFileList, setInvoiceStampFileList] = useState([]);
  const [currentConfig, setCurrentConfig] = useState(readCachedSystemConfig());
  const [farmerExpenseTypes, setFarmerExpenseTypes] = useState([]);

  const populate = (config) => {
    form.setFieldsValue({
      site_name: config.site_name,
      login_logo_url: config.login_logo_url || config.login_logo || "",
      sidebar_logo_url: config.sidebar_logo_url || config.sidebar_logo || "",
      currency_code: config.currency_code || "IQD",
      secondary_currency_code: config.secondary_currency_code || "",
      currency_display: config.currency_display || "code",
      locale: config.locale || "en-US",
      fraction_digits: config.fraction_digits ?? 0,
      pos_commission_percent: config.pos_commission_percent ?? 0,
      opening_cash: config.opening_cash ?? 0,
      accountant_farmer_expense_type: config.accountant_farmer_expense_type || null,
      accountant_farmer_expense_label: config.accountant_farmer_expense_label || "",
      print_color_primary: config.print_color_primary || DEFAULT_PRINT_COLORS.primary,
      print_color_accent: config.print_color_accent || DEFAULT_PRINT_COLORS.accent,
      print_color_soft: config.print_color_soft || DEFAULT_PRINT_COLORS.soft,
      business_day_start: config.business_day_start || "00:00",
      pos_rounding_step: config.pos_rounding_step ?? 250,
      pos_rounding_threshold: config.pos_rounding_threshold ?? 25,
      pos_commission_payer: config.pos_commission_payer || "customer",
      pos_commission_mode: config.pos_commission_mode || "percent",
      pos_commission_per_qty_amount: config.pos_commission_per_qty_amount ?? 0,
      pos_enable_drafts: config.pos_enable_drafts !== false,
      default_language: config.default_language || localStorage.getItem("selectedLanguage") || "en",
      invoice_company_name: config.invoice_company_name ?? "",
      invoice_tagline: config.invoice_tagline ?? "",
      invoice_logo_url: config.invoice_logo_url || config.invoice_logo || "",
      invoice_stamp_image_url: config.invoice_stamp_image_url || config.invoice_stamp_image || "",
      invoice_address: config.invoice_address ?? "",
      invoice_phones: config.invoice_phones ?? "",
      invoice_email: config.invoice_email ?? "",
      invoice_website: config.invoice_website ?? "",
      invoice_footer_note: config.invoice_footer_note ?? "",
      invoice_watermark_enabled: config.invoice_watermark_enabled !== false,
      invoice_watermark_opacity: config.invoice_watermark_opacity ?? 0.07,
      date_filter_presets: normalizeDateFilterPresets(config.date_filter_presets),
    });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [config, types] = await Promise.all([
          fetchSystemConfig(),
          isAdmin ? fetchExpenseTypes({ all: false }) : Promise.resolve([]),
        ]);
        setFarmerExpenseTypes(Array.isArray(types) ? types : []);
        setCurrentConfig(config);
        populate(config);
      } catch {
        const cached = readCachedSystemConfig();
        if (cached) { setCurrentConfig(cached); populate(cached); message.warning(t("settingsWarnCached")); }
        else message.error(t("settingsErrLoad"));
      } finally {
        setLoading(false);
      }
    };
    load();
  // populate is stable enough for initial load; form/t/isAdmin are the real triggers
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, t, isAdmin]);

  const handleSave = async () => {
    try {
      // Tabs unmount inactive fields — validateFields() only returns the active tab.
      // That used to overwrite commission/currency with hardcoded defaults (0 / IQD / code).
      await form.validateFields();
      const values = form.getFieldsValue(true);
      const prev = currentConfig || readCachedSystemConfig() || {};

      const pickStr = (v, fallback = "") => {
        const s = v == null ? "" : String(v).trim();
        return s || fallback;
      };
      const pickNum = (v, fallback) => {
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? n : (Number.isFinite(Number(fallback)) ? Number(fallback) : 0);
      };
      const pickColor = (v, fallback) => {
        if (v && typeof v === "object" && typeof v.toHexString === "function") {
          return v.toHexString();
        }
        const s = String(v ?? "").trim();
        return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s) ? s : fallback;
      };

      const payload = {
        site_name: pickStr(values.site_name, prev.site_name || "IQ Price"),
        login_logo_url: values.login_logo_url ?? prev.login_logo_url ?? "",
        sidebar_logo_url: values.sidebar_logo_url ?? prev.sidebar_logo_url ?? "",
        currency_code: pickStr(values.currency_code, prev.currency_code || "IQD"),
        secondary_currency_code: (values.secondary_currency_code ?? prev.secondary_currency_code ?? "").toString().trim() || null,
        currency_display: pickStr(values.currency_display, prev.currency_display || "code"),
        locale: pickStr(values.locale, prev.locale || "en-US"),
        fraction_digits: pickNum(values.fraction_digits, prev.fraction_digits ?? 0),
        invoice_company_name: values.invoice_company_name ?? prev.invoice_company_name ?? "",
        invoice_tagline: values.invoice_tagline ?? prev.invoice_tagline ?? "",
        invoice_logo_url: values.invoice_logo_url ?? prev.invoice_logo_url ?? "",
        invoice_stamp_image_url: values.invoice_stamp_image_url ?? prev.invoice_stamp_image_url ?? "",
        invoice_address: values.invoice_address ?? prev.invoice_address ?? "",
        invoice_phones: values.invoice_phones ?? prev.invoice_phones ?? "",
        invoice_email: values.invoice_email ?? prev.invoice_email ?? "",
        invoice_website: values.invoice_website ?? prev.invoice_website ?? "",
        invoice_footer_note: values.invoice_footer_note ?? prev.invoice_footer_note ?? "",
        pos_commission_percent: pickNum(values.pos_commission_percent, prev.pos_commission_percent ?? 0),
        business_day_start: pickStr(values.business_day_start, prev.business_day_start || "00:00"),
        pos_rounding_step: pickNum(values.pos_rounding_step, prev.pos_rounding_step ?? 250),
        pos_rounding_threshold: pickNum(values.pos_rounding_threshold, prev.pos_rounding_threshold ?? 25),
        pos_commission_payer: pickStr(values.pos_commission_payer, prev.pos_commission_payer || "customer") === "farmer" ? "farmer" : "customer",
        pos_commission_mode: (() => {
          const m = pickStr(values.pos_commission_mode, prev.pos_commission_mode || "percent");
          return m === "per_qty" || m === "mix" ? m : "percent";
        })(),
        pos_commission_per_qty_amount: pickNum(values.pos_commission_per_qty_amount, prev.pos_commission_per_qty_amount ?? 0),
        pos_enable_drafts:
          values.pos_enable_drafts === undefined || values.pos_enable_drafts === null
            ? prev.pos_enable_drafts !== false
            : Boolean(values.pos_enable_drafts),
        default_language: pickStr(values.default_language, prev.default_language || "en"),
        invoice_watermark_enabled:
          values.invoice_watermark_enabled === undefined || values.invoice_watermark_enabled === null
            ? prev.invoice_watermark_enabled !== false
            : Boolean(values.invoice_watermark_enabled),
        invoice_watermark_opacity: pickNum(
          values.invoice_watermark_opacity,
          prev.invoice_watermark_opacity ?? 0.07
        ),
        print_color_primary: pickColor(values.print_color_primary, prev.print_color_primary || DEFAULT_PRINT_COLORS.primary),
        print_color_accent: pickColor(values.print_color_accent, prev.print_color_accent || DEFAULT_PRINT_COLORS.accent),
        print_color_soft: pickColor(values.print_color_soft, prev.print_color_soft || DEFAULT_PRINT_COLORS.soft),
        date_filter_presets: normalizeDateFilterPresets(
          values.date_filter_presets ?? prev.date_filter_presets ?? DEFAULT_DATE_FILTER_PRESETS
        ).join(","),
      };
      if (isAdmin) {
        payload.opening_cash = pickNum(values.opening_cash, prev.opening_cash ?? 0);
        payload.accountant_farmer_expense_type =
          values.accountant_farmer_expense_type !== undefined
            ? (values.accountant_farmer_expense_type || null)
            : (prev.accountant_farmer_expense_type || null);
        payload.accountant_farmer_expense_label =
          values.accountant_farmer_expense_label !== undefined
            ? ((values.accountant_farmer_expense_label || "").trim() || null)
            : (prev.accountant_farmer_expense_label || null);
      }
      if (loginFileList[0]?.originFileObj) payload.login_logo = loginFileList[0].originFileObj;
      if (sidebarFileList[0]?.originFileObj) payload.sidebar_logo = sidebarFileList[0].originFileObj;
      if (invoiceLogoFileList[0]?.originFileObj) payload.invoice_logo = invoiceLogoFileList[0].originFileObj;
      if (invoiceStampFileList[0]?.originFileObj) payload.invoice_stamp_image = invoiceStampFileList[0].originFileObj;

      setSaving(true);
      const saved = await saveSystemConfig(payload);
      setCurrentConfig(saved);
      if (payload.default_language) {
        i18nModule.changeLanguage(payload.default_language);
        localStorage.setItem("selectedLanguage", payload.default_language);
        localStorage.setItem("i18nextLng", payload.default_language);
      }
      setLoginFileList([]); setSidebarFileList([]); setInvoiceLogoFileList([]); setInvoiceStampFileList([]);
      populate(saved);
      message.success(t("settingsSaveSuccess"));
    } catch (error) {
      if (error?.errorFields) return;
      const errs = error?.response?.data?.errors;
      const firstField = errs && typeof errs === "object" ? Object.values(errs).flat()?.[0] : null;
      message.error(firstField || error?.response?.data?.message || t("settingsSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const SaveBtn = () => (
    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave} style={{ borderRadius: 9, height: 40, fontWeight: 600, marginTop: 8 }}>
      {t("settingsSave")}
    </Button>
  );

  const tabs = [
    {
      key: "general",
      label: <span><GlobalOutlined style={{ marginInlineEnd: 6 }} />{t("settings.general.tab", { defaultValue: "General" })}</span>,
      children: (
        <FormCard
          title={t("settings.general.title", { defaultValue: "General settings" })}
          subtitle={t("settings.general.subtitle", { defaultValue: "Site name and language preferences" })}
        >
          <Form.Item name="site_name" label={t("settingsWebsiteName")} rules={[{ required: true, message: t("settingsWebsiteNameRequired", "Please enter a site name.") }]}>
            <Input placeholder={t("settingsWebsiteNamePlaceholder")} style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="default_language"
            label={t("settings.general.defaultLanguage", { defaultValue: "Default language" })}
            tooltip={t("settings.general.defaultLanguageHint", { defaultValue: "Language will switch for all users when saved." })}
          >
            <Select style={{ ...inputStyle, height: undefined }}>
              {LANGUAGES.map((l) => <Select.Option key={l.value} value={l.value}>{l.label}</Select.Option>)}
            </Select>
          </Form.Item>
          <SaveBtn />
        </FormCard>
      ),
    },
    {
      key: "currency",
      label: <span><DollarOutlined style={{ marginInlineEnd: 6 }} />{t("settingsCurrencySection")}</span>,
      children: (
        <FormCard
          title={t("settingsCurrencySection")}
          subtitle={t("settings.currency.subtitle", { defaultValue: "Currency codes, locale, and display format" })}
        >
          <div style={grid2}>
            <Form.Item name="currency_code" label={t("settingsMainCurrencyCode")} rules={[{ required: true, message: t("settingsCurrencyCodeRequired", "Please enter a currency code.") }]}>
              <Input placeholder="IQD" maxLength={10} style={inputStyle} />
            </Form.Item>
            <Form.Item name="secondary_currency_code" label={t("settingsSecondaryCurrencyOptional")}>
              <Input placeholder={t("settingsSecondaryCurrencyPlaceholder")} maxLength={10} style={inputStyle} />
            </Form.Item>
            <Form.Item name="currency_display" label={t("settingsCurrencyDisplay")} rules={[{ required: true, message: t("settingsCurrencyDisplayRequired", "Please select a currency display format.") }]}>
              <Select style={{ ...inputStyle, height: undefined }}
                options={[
                  { value: "code", label: t("settingsCurrencyDisplayCode") },
                  { value: "symbol", label: t("settingsCurrencyDisplaySymbol") },
                  { value: "name", label: t("settingsCurrencyDisplayName") },
                ]}
              />
            </Form.Item>
            <Form.Item name="locale" label={t("settingsLocale")} rules={[{ required: true, message: t("settingsLocaleRequired", "Please enter a locale.") }]}>
              <Input placeholder={t("settingsLocalePlaceholder")} maxLength={35} style={inputStyle} />
            </Form.Item>
            <Form.Item name="fraction_digits" label={t("settingsFractionDigits")} rules={[{ required: true, message: t("settingsFractionDigitsRequired", "Please enter the number of fraction digits.") }]}>
              <InputNumber min={0} max={6} style={{ width: "100%", borderRadius: 9, height: 42 }} />
            </Form.Item>
          </div>
          <SaveBtn />
        </FormCard>
      ),
    },
    ...(isAdmin ? [{
      key: "cash",
      label: <span><WalletOutlined style={{ marginInlineEnd: 6 }} />{t("settingsCashSection", "Cash")}</span>,
      children: (
        <FormCard
          title={t("settingsOpeningCashTitle", "Opening cash")}
          subtitle={t("settingsOpeningCashHint", "First cash put into the system. Only admin can set this. Cash on hand = opening + collected − paid to farmers − company expenses − returns.")}
        >
          <Form.Item
            name="opening_cash"
            label={t("settingsOpeningCash", "Opening cash amount")}
            rules={[{ required: true, message: t("settingsOpeningCashRequired", "Enter the opening cash") }]}
            extra={t("settingsOpeningCashExtra", "Use this once when you start the system with money already in the drawer / safe.")}
          >
            <InputNumber
              min={0}
              step={1000}
              style={{ width: "100%", maxWidth: 320, borderRadius: 9, height: 42 }}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => (v || "").replace(/,/g, "")}
            />
          </Form.Item>
          <Form.Item
            name="accountant_farmer_expense_type"
            label={t("settingsAccountantFarmerExpenseType", "Accountant dashboard — farmer expense type")}
            extra={t(
              "settingsAccountantFarmerExpenseTypeHint",
              "Only this setting controls which farmer expense type appears as a dedicated card on the Accountant Dashboard. It cannot be changed from the dashboard filters."
            )}
          >
            <Select
              allowClear
              placeholder={t("settingsAccountantFarmerExpenseTypePlaceholder", "Select expense type")}
              style={{ width: "100%", maxWidth: 420, borderRadius: 9 }}
              options={(farmerExpenseTypes || []).map((x) => ({
                value: x.code,
                label: x.name || x.code,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="accountant_farmer_expense_label"
            label={t("settingsAccountantFarmerExpenseLabel", "Card label")}
            extra={t(
              "settingsAccountantFarmerExpenseLabelHint",
              "Custom title shown on the Accountant Dashboard card. Leave empty to use the expense type name."
            )}
          >
            <Input
              placeholder={t("settingsAccountantFarmerExpenseLabelPlaceholder", "e.g. Watching / پاسەوانی")}
              style={{ ...inputStyle, maxWidth: 420 }}
              maxLength={255}
            />
          </Form.Item>
          <SaveBtn />
        </FormCard>
      ),
    }] : []),
    {
      key: "branding",
      label: <span><SkinOutlined style={{ marginInlineEnd: 6 }} />{t("settingsBrandingSection")}</span>,
      children: (
        <FormCard
          title={t("settingsBrandingSection")}
          subtitle={t("settings.branding.subtitle", { defaultValue: "Logo images for login screen and sidebar" })}
        >
          <div style={grid2}>
            <div>
              <Form.Item name="login_logo_url" label={t("settingsLoginLogoUrl")}>
                <Input placeholder={t("settingsLoginLogoUrlPlaceholder")} style={inputStyle} />
              </Form.Item>
              <UploadField label={t("settingsUploadLoginLogo")} fileList={loginFileList} onChange={setLoginFileList} hint={t("settingsSelectLoginLogo")} />
            </div>
            <div>
              <Form.Item name="sidebar_logo_url" label={t("settingsSidebarLogoUrl")}>
                <Input placeholder={t("settingsSidebarLogoUrlPlaceholder")} style={inputStyle} />
              </Form.Item>
              <UploadField label={t("settingsUploadSidebarLogo")} fileList={sidebarFileList} onChange={setSidebarFileList} hint={t("settingsSelectSidebarLogo")} />
            </div>
          </div>
          <SaveBtn />
        </FormCard>
      ),
    },
    {
      key: "invoice",
      label: <span><FileTextOutlined style={{ marginInlineEnd: 6 }} />{t("settingsInvoiceSection")}</span>,
      children: (
        <FormCard title={t("settingsInvoiceSection")} subtitle={t("settingsInvoiceSectionHint")}>
          <div style={grid2}>
            <Form.Item name="invoice_company_name" label={t("settingsInvoiceCompanyTitle")}>
              <Input placeholder={t("settingsInvoiceCompanyTitlePlaceholder")} style={inputStyle} />
            </Form.Item>
            <Form.Item name="invoice_tagline" label={t("settingsInvoiceTagline")}>
              <Input placeholder={t("settingsInvoiceTaglinePlaceholder")} style={inputStyle} />
            </Form.Item>
            <Form.Item name="invoice_email" label={t("settingsInvoiceEmail")}>
              <Input type="email" placeholder={t("settingsInvoiceEmailPlaceholder")} style={inputStyle} />
            </Form.Item>
            <Form.Item name="invoice_website" label={t("settingsInvoiceWebsite")}>
              <Input placeholder={t("settingsInvoiceWebsitePlaceholder")} style={inputStyle} />
            </Form.Item>
          </div>
          <Form.Item name="invoice_address" label={t("settingsInvoiceAddress")}>
            <Input.TextArea rows={2} placeholder={t("settingsInvoiceAddressPlaceholder")} style={{ borderRadius: 9 }} />
          </Form.Item>
          <Form.Item
            name="invoice_phones"
            label={t("settingsInvoicePhones")}
            tooltip={t("settingsInvoicePhonesHint", "Shown under Managers on the POS receipt. One manager per line: name - phone.")}
          >
            <Input.TextArea
              rows={4}
              placeholder={t("settingsInvoicePhonesPlaceholder")}
              style={{ borderRadius: 9 }}
            />
          </Form.Item>
          <Form.Item name="invoice_footer_note" label={t("settingsInvoiceFooterNote")}>
            <Input.TextArea rows={2} placeholder={t("settingsInvoiceFooterNotePlaceholder")} style={{ borderRadius: 9 }} />
          </Form.Item>
          <div style={grid2}>
            <div>
              <Form.Item name="invoice_logo_url" label={t("settingsInvoiceLogoUrl")}>
                <Input placeholder={t("settingsInvoiceLogoUrlPlaceholder")} style={inputStyle} />
              </Form.Item>
              <UploadField label={t("settingsInvoiceLogoUpload")} fileList={invoiceLogoFileList} onChange={setInvoiceLogoFileList} hint={t("settingsInvoiceLogoPick")} />
            </div>
            <div>
              <Form.Item name="invoice_stamp_image_url" label={t("settingsInvoiceStampUrl")}>
                <Input placeholder={t("settingsInvoiceStampUrlPlaceholder")} style={inputStyle} />
              </Form.Item>
              <UploadField label={t("settingsInvoiceStampUpload")} fileList={invoiceStampFileList} onChange={setInvoiceStampFileList} hint={t("settingsInvoiceStampPick")} />
            </div>
          </div>
          <div style={grid2}>
            <Form.Item name="invoice_watermark_enabled" label={t("settingsInvoiceWatermarkEnabled")} valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="invoice_watermark_opacity" label={t("settingsInvoiceWatermarkOpacity")} tooltip={t("settingsInvoiceWatermarkOpacityHint")}>
              <InputNumber min={0.02} max={0.5} step={0.01} style={{ width: "100%", borderRadius: 9, height: 42 }} />
            </Form.Item>
          </div>
          <div style={{ marginTop: 8, marginBottom: 4, fontWeight: 700, color: T.text }}>
            {t("settingsPrintColorsTitle", "Print colors")}
          </div>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 12 }}>
            {t("settingsPrintColorsHint", "Used on farmer payment / settlement slips and customer payment receipts.")}
          </div>
          <div style={grid2}>
            <Form.Item
              name="print_color_primary"
              label={t("settingsPrintColorPrimary", "Primary (text & borders)")}
              getValueFromEvent={(c) => (typeof c === "string" ? c : c?.toHexString?.() || DEFAULT_PRINT_COLORS.primary)}
              getValueProps={(v) => ({ value: v || DEFAULT_PRINT_COLORS.primary })}
            >
              <ColorPicker showText format="hex" />
            </Form.Item>
            <Form.Item
              name="print_color_accent"
              label={t("settingsPrintColorAccent", "Accent (titles & amounts)")}
              getValueFromEvent={(c) => (typeof c === "string" ? c : c?.toHexString?.() || DEFAULT_PRINT_COLORS.accent)}
              getValueProps={(v) => ({ value: v || DEFAULT_PRINT_COLORS.accent })}
            >
              <ColorPicker showText format="hex" />
            </Form.Item>
            <Form.Item
              name="print_color_soft"
              label={t("settingsPrintColorSoft", "Soft (headers & bands)")}
              getValueFromEvent={(c) => (typeof c === "string" ? c : c?.toHexString?.() || DEFAULT_PRINT_COLORS.soft)}
              getValueProps={(v) => ({ value: v || DEFAULT_PRINT_COLORS.soft })}
            >
              <ColorPicker showText format="hex" />
            </Form.Item>
          </div>
          <SaveBtn />
        </FormCard>
      ),
    },
    {
      key: "filters",
      label: <span><CalendarOutlined style={{ marginInlineEnd: 6 }} />{t("settingsDateFiltersTab", "Date filters")}</span>,
      children: (
        <FormCard
          title={t("settingsDateFiltersTitle", "Date & time filter presets")}
          subtitle={t(
            "settingsDateFiltersHint",
            "Choose which quick ranges appear on dashboards, orders, returns, farmer pages, and reports. From/to also support time."
          )}
        >
          <Form.Item
            name="date_filter_presets"
            label={t("settingsDateFilterPresets", "Enabled presets")}
            extra={t(
              "settingsDateFilterPresetsExtra",
              "These buttons appear wherever date filters are used. Custom from/to with time is always available."
            )}
          >
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%", borderRadius: 9 }}
              placeholder={t("settingsDateFilterPresetsPlaceholder", "Select presets")}
              options={DATE_FILTER_PRESET_DEFS.map((d) => ({
                value: d.key,
                label: t(d.labelKey, d.defaultLabel),
              }))}
            />
          </Form.Item>
          <SaveBtn />
        </FormCard>
      ),
    },
    {
      key: "pos",
      label: <span><ShopOutlined style={{ marginInlineEnd: 6 }} />{t("settings.pos.tab", { defaultValue: "POS" })}</span>,
      children: (
        <FormCard
          title={t("settings.pos.title", { defaultValue: "POS settings" })}
          subtitle={t("settings.pos.subtitle", { defaultValue: "Point of sale commission configuration" })}
        >
          <Form.Item
            name="pos_commission_percent"
            label={t("settings.pos.commissionLabel", { defaultValue: "Commission % (shop keeps from each sale)" })}
            tooltip={t("settings.pos.commissionHint", { defaultValue: "Percentage the shop retains as commission from every consignment sale." })}
            rules={[{ required: true, type: "number", min: 0, max: 100, message: t("settingsPosCommissionRequired", "Please enter a commission percentage from 0 to 100.") }]}
          >
            <InputNumber min={0} max={100} step={0.5} addonAfter="%" style={{ width: "100%", borderRadius: 9, height: 42 }} />
          </Form.Item>
          <Form.Item
            name="pos_commission_payer"
            label={t("posCommissionPayerLabel", "Who pays the commission")}
            tooltip={t("posCommissionPayerHint", "Customer: commission is added on top of the subtotal. Farmer/Vendor: the customer pays exactly the subtotal, and the commission is deducted from the farmer/vendor's payout instead.")}
          >
            <Select
              style={{ borderRadius: 9 }}
              options={[
                { value: "customer", label: t("posCommissionPayerCustomer", "Customer (added on top)") },
                { value: "farmer", label: t("posCommissionPayerFarmer", "Farmer / Vendor (deducted from payout)") },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="pos_commission_mode"
            label={t("settings.pos.commissionModeLabel", { defaultValue: "Commission mode" })}
            tooltip={t("settings.pos.commissionModeHint", { defaultValue: "Percent: commission is a % of the sale subtotal (set above). Flat per case/qty: a flat amount is charged per case sold instead. Mix: both the percent and the flat per-case amount are charged together on every sale." })}
          >
            <Select
              style={{ borderRadius: 9 }}
              options={[
                { value: "percent", label: t("settings.pos.commissionModePercent", { defaultValue: "Percent of subtotal" }) },
                { value: "per_qty", label: t("settings.pos.commissionModePerQty", { defaultValue: "Flat amount per case/qty" }) },
                { value: "mix", label: t("settings.pos.commissionModeMix", { defaultValue: "Mix (percent + flat per case/qty)" }) },
              ]}
            />
          </Form.Item>
          <Form.Item shouldUpdate={(prev, cur) => prev.pos_commission_mode !== cur.pos_commission_mode} noStyle>
            {({ getFieldValue }) => {
              const mode = getFieldValue("pos_commission_mode");
              return mode === "per_qty" || mode === "mix" ? (
                <Form.Item
                  name="pos_commission_per_qty_amount"
                  label={t("settings.pos.commissionPerQtyLabel", { defaultValue: "Flat amount per case/qty" })}
                  tooltip={t("settings.pos.commissionPerQtyHint", { defaultValue: "Charged once per case/qty unit on a sale line. A line with no case count entered (sold purely by weight, e.g. loose kg) is not charged." })}
                  rules={[{ required: true, type: "number", min: 0, message: t("settingsPosCommissionPerQtyRequired", "Please enter a per-case commission amount.") }]}
                >
                  <InputNumber min={0} step={1} style={{ width: "100%", borderRadius: 9, height: 42 }} />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
          <Form.Item
            name="business_day_start"
            label={t("businessDayStartLabel", "New business day starts at")}
            tooltip={t("businessDayStartHint", "The trading day begins at this time. Sales before it count for the previous market day (e.g. 06:00 for an early farmers market).")}
          >
            <Select
              showSearch
              style={{ borderRadius: 9 }}
              options={Array.from({ length: 48 }, (_, i) => {
                const h = String(Math.floor(i / 2)).padStart(2, "0");
                const m = i % 2 === 0 ? "00" : "30";
                return { value: `${h}:${m}`, label: `${h}:${m}` };
              })}
            />
          </Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Form.Item
              name="pos_rounding_step"
              label={t("roundingStepLabel", "Cash denomination (round to)")}
              tooltip={t("roundingStepHint", "Final totals snap to multiples of this amount, e.g. 250 for Iraqi dinar (250, 500, 750, 1000 …). Line prices and commission stay exact.")}
            >
              <InputNumber min={1} step={50} style={{ width: "100%", borderRadius: 9, height: 42 }} />
            </Form.Item>
            <Form.Item
              name="pos_rounding_threshold"
              label={t("roundingThresholdLabel", "Round-up threshold")}
              tooltip={t("roundingThresholdHint", "Applied only to the final total: round up to the next denomination when the amount is at least this much over the lower one (e.g. 25 → 1525 becomes 1750, 1524 stays 1500).")}
            >
              <InputNumber min={0} step={5} style={{ width: "100%", borderRadius: 9, height: 42 }} />
            </Form.Item>
          </div>
          <Form.Item
            name="pos_enable_drafts"
            label={t("posEnableDraftsLabel", "Enable drafts")}
            tooltip={t("posEnableDraftsHint", "When off, the Drafts button and Drafts screen are hidden on the POS.")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <SaveBtn />
        </FormCard>
      ),
    },
  ];

  return (
    <ListPage
      breadcrumb={[t("navigation.administration", { defaultValue: "Administration" }), t("systemSettings")]}
      title={t("systemSettings")}
      subtitle={currentConfig ? t("settingsCurrentSiteName", { name: currentConfig.site_name }) : ""}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" preserve className="system-settings-form">
          <Tabs
            className="system-settings-tabs"
            items={tabs.map((tab) => ({ ...tab, forceRender: true }))}
            tabPosition="left"
            style={{ minHeight: 400 }}
          />
        </Form>
      </Spin>
      <div style={{ textAlign: "center", fontSize: 12, color: T.muted, marginTop: 16 }}>
        {t("settingsAppVersion", "Version {{version}}", { version: packageJson.version })}
      </div>
    </ListPage>
  );
};

export default SystemSettings;
